import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AcpAgentCatalogData } from '@/app/api/acp/agents/route';
import {
  acpAgentPreferenceId,
  DEFAULT_ACP_AGENT_IDS,
  fetchAcpAgentCatalog,
  registryIdForLocalAgent,
  selectedAcpAgentIds,
} from '@/lib/agent-runtime/acp-agent-catalog';
import type { AgentRuntimeOverride, AppSettings } from '@/lib/app-settings';
import { errorMessage } from '@/lib/error-message';
import { isLocalMachine } from '@/lib/local-runtime';
import { discoverNativeAgents, type NativeAgent } from '@/lib/native-desktop';
import { settingsPreview } from '@/lib/settings-page/settings-preview';

export function deriveAgentVisibility({
  agentIds,
  catalogAgents,
  runtimeAgents,
  showAllAgents,
}: {
  agentIds: readonly string[] | null;
  catalogAgents: readonly AcpAgentCatalogData[];
  runtimeAgents: readonly NativeAgent[];
  showAllAgents: boolean;
}) {
  const localRegistryIds = new Set(
    runtimeAgents
      .map((agent) => registryIdForLocalAgent(agent))
      .filter((id): id is string => id !== null),
  );
  const onDemandAgents = catalogAgents.filter((agent) => !localRegistryIds.has(agent.id));
  const selectedIds = selectedAcpAgentIds(agentIds);
  const visibleRuntimeAgents = showAllAgents
    ? runtimeAgents
    : runtimeAgents.filter((agent) => selectedIds.has(acpAgentPreferenceId(agent)));
  const visibleOnDemandAgents = showAllAgents
    ? onDemandAgents
    : onDemandAgents.filter((agent) => selectedIds.has(agent.id));
  const selectedAgentCount =
    runtimeAgents.filter((agent) => selectedIds.has(acpAgentPreferenceId(agent))).length +
    onDemandAgents.filter((agent) => selectedIds.has(agent.id)).length;

  return {
    hiddenAgentCount:
      runtimeAgents.length +
      onDemandAgents.length -
      visibleRuntimeAgents.length -
      visibleOnDemandAgents.length,
    onDemandAgents,
    selectedAgentCount,
    selectedIds,
    visibleOnDemandAgents,
    visibleRuntimeAgents,
  };
}

export function updateRuntimeOverrides(
  overrides: AppSettings['agentRuntimeOverrides'],
  id: string,
  override?: AgentRuntimeOverride,
): AppSettings['agentRuntimeOverrides'] {
  const next = { ...overrides };
  if (override === undefined) delete next[id];
  else next[id] = override;
  return next;
}

export function updateSelectedAgents(
  agentIds: AppSettings['agentIds'],
  id: string,
  enabled: boolean,
): string[] {
  const next = new Set(agentIds ?? DEFAULT_ACP_AGENT_IDS);
  if (enabled) next.add(id);
  else next.delete(id);
  return [...next];
}

export function useAgentSettingsPanel({
  agentIds,
  agentRuntimeOverrides,
  onAgentIdsChange,
  onAgentRuntimeOverridesChange,
}: {
  agentIds: AppSettings['agentIds'];
  agentRuntimeOverrides: AppSettings['agentRuntimeOverrides'];
  onAgentIdsChange: (value: AppSettings['agentIds']) => void;
  onAgentRuntimeOverridesChange: (value: AppSettings['agentRuntimeOverrides']) => void;
}) {
  const [desktop, setDesktop] = useState<boolean | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [runtimeAgents, setRuntimeAgents] = useState<NativeAgent[]>([]);
  const [catalogAgents, setCatalogAgents] = useState<AcpAgentCatalogData[]>([]);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());
  const [showAllAgents, setShowAllAgents] = useState(false);

  const refresh = useCallback(async () => {
    const preview = settingsPreview()?.agents;
    if (preview !== undefined) {
      setRuntimeAgents(preview.runtime);
      setCatalogAgents(preview.catalog);
      setLoaded(true);
      return;
    }
    if (!isLocalMachine()) return;
    try {
      const [discovered, catalog] = await Promise.all([
        discoverNativeAgents(),
        fetchAcpAgentCatalog().then(
          (agents) => ({ agents, error: null }),
          (reason: unknown) => ({ agents: [], error: errorMessage(reason) }),
        ),
      ]);
      setRuntimeAgents(discovered);
      setCatalogAgents(catalog.agents);
      setCatalogError(catalog.error);
      setError(null);
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    const native = isLocalMachine() || settingsPreview()?.local === true;
    setDesktop(native);
    if (native) void refresh();
  }, [refresh]);

  const updateRuntimeOverride = useCallback(
    (id: string, override?: AgentRuntimeOverride) => {
      onAgentRuntimeOverridesChange(updateRuntimeOverrides(agentRuntimeOverrides, id, override));
    },
    [agentRuntimeOverrides, onAgentRuntimeOverridesChange],
  );

  const toggleExpanded = useCallback((id: string) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const updateAgentSelection = useCallback(
    (id: string, enabled: boolean) => {
      onAgentIdsChange(updateSelectedAgents(agentIds, id, enabled));
    },
    [agentIds, onAgentIdsChange],
  );

  const visibility = useMemo(
    () => deriveAgentVisibility({ agentIds, catalogAgents, runtimeAgents, showAllAgents }),
    [agentIds, catalogAgents, runtimeAgents, showAllAgents],
  );

  return {
    catalogAgents,
    catalogError,
    desktop,
    error,
    expanded,
    loaded,
    refresh,
    runtimeAgents,
    setShowAllAgents,
    showAllAgents,
    toggleExpanded,
    updateAgentSelection,
    updateRuntimeOverride,
    ...visibility,
  };
}
